<template>
    <div
        class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 sm:flex-wrap pt-2 sm:pt-0 pb-2"
    >
        <!-- Mobile: Period picker + nav (above) -->
        <div class="flex sm:hidden items-center gap-2">
            <div class="relative flex-1">
                <button
                    type="button"
                    class="w-full flex items-center justify-between gap-2 pl-4 pr-3 py-2 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                    @click="presetOpen = true"
                >
                    <div class="flex items-center gap-2 min-w-0">
                        <Icon
                            icon="calendar"
                            class="w-5 h-5 text-slate-800 dark:text-slate-100 shrink-0"
                            svgClass="fill-none"
                            pathClass="fill-current"
                        />
                        <span
                            class="text-slate-800 dark:text-slate-100 font-inter text-sm font-medium leading-5 truncate"
                        >
                            {{ rangeLabel }}
                        </span>
                    </div>
                    <Icon
                        icon="chevron-down-small"
                        class="w-5 h-5 text-slate-800 dark:text-slate-100 shrink-0"
                    />
                </button>
            </div>

            <div
                class="flex items-center rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm shrink-0"
            >
                <button
                    type="button"
                    class="flex p-2 items-center rounded-l cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:z-10"
                    aria-label="Previous period"
                    @click="store.shiftInsightsRange(-1)"
                >
                    <Icon
                        icon="chevron-left-small"
                        class="w-5 h-5 text-slate-800 dark:text-slate-100"
                    />
                </button>
                <div class="w-px self-stretch bg-slate-200 dark:bg-slate-700"></div>
                <button
                    type="button"
                    :class="[
                        'flex p-2 items-center rounded-r focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:z-10',
                        canNavigateNext
                            ? 'cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-700'
                            : 'opacity-50 cursor-default',
                    ]"
                    :disabled="!canNavigateNext"
                    aria-label="Next period"
                    @click="canNavigateNext && store.shiftInsightsRange(1)"
                >
                    <Icon
                        icon="chevron-right-small"
                        class="w-5 h-5 text-slate-800 dark:text-slate-100"
                    />
                </button>
            </div>
        </div>

        <!-- Mobile: Filters button (single button opens modal) -->
        <button
            class="btn-base btn-white sm:!hidden self-start"
            @click="mobileFiltersOpen = true"
        >
            <Icon icon="filters" class="w-4 h-4" />
            Filters
            <span
                v-if="hasActiveInsightsFilters"
                class="w-5 h-5 text-white rounded-full inline-flex items-center justify-center bg-slate-800 text-xs font-medium"
            >
                {{ activeFiltersCount }}
            </span>
        </button>

        <div class="hidden sm:flex flex-wrap items-center gap-4">
            <ButtonPopover
                v-if="userOptions.length > 1"
                buttonClass="btn-base btn-white !pl-4 !pr-3 h-10"
                :active-button-class="activeFilterBtnClass"
            >
                <template #button-content>
                    <span class="text-primary text-sm font-medium">User</span>
                    <Transition name="fade">
                        <span
                            v-if="selectedUsers.length >= 1"
                            class="w-5 h-5 text-white rounded-full inline-flex items-center justify-center bg-slate-800 text-xs font-medium"
                        >
                            {{ selectedUsers.length }}
                        </span>
                    </Transition>
                    <Icon
                        icon="chevron-down-small"
                        class="w-5 h-5 icons-primary"
                    />
                </template>
                <template #default>
                    <ul>
                        <li v-for="user in userOptions" :key="user.id">
                            <button
                                type="button"
                                class="w-full p-2 text-sm text-gray-800 dark:text-gray-100 rounded text-left hover:bg-gray-100 dark:hover:bg-slate-700 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none transition-colors flex items-center gap-2"
                                @click="toggleUser(user)"
                            >
                                <input
                                    type="checkbox"
                                    class="rounded text-10x-orange"
                                    :checked="isUserSelected(user)"
                                    @click.stop
                                    @change="toggleUser(user)"
                                />
                                <div
                                    class="flex h-5 w-5 p-1 justify-center items-center rounded-full bg-slate-100 dark:bg-slate-700"
                                >
                                    <Icon
                                        :icon="
                                            user.is_business
                                                ? 'business'
                                                : 'user'
                                        "
                                        class="w-3 h-3 text-slate-500 dark:text-slate-400"
                                    />
                                </div>
                                <span>{{ user.name }}</span>
                            </button>
                        </li>
                        <li v-if="selectedUsers.length >= 1">
                            <button
                                type="button"
                                class="w-full p-2 text-sm text-gray-800 dark:text-gray-100 rounded text-left hover:bg-gray-100 dark:hover:bg-slate-700 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none transition-colors flex items-center gap-2"
                                @click="store.resetInsightsUserFilter()"
                            >
                                <span>Reset</span>
                            </button>
                        </li>
                    </ul>
                </template>
            </ButtonPopover>

            <InsightsCardDropdown />

            <ButtonPopover
                v-if="bankOptions.length > 1"
                buttonClass="btn-base btn-white !pl-4 !pr-3 h-10"
                :active-button-class="activeFilterBtnClass"
            >
                <template #button-content>
                    <span class="text-primary text-sm font-medium">Bank</span>
                    <Transition name="fade">
                        <span
                            v-if="selectedBanks.length >= 1"
                            class="w-5 h-5 text-white rounded-full inline-flex items-center justify-center bg-slate-800 text-xs font-medium"
                        >
                            {{ selectedBanks.length }}
                        </span>
                    </Transition>
                    <Icon
                        icon="chevron-down-small"
                        class="w-5 h-5 icons-primary"
                    />
                </template>
                <template #default>
                    <ul>
                        <li v-for="bank in bankOptions" :key="bank.id">
                            <button
                                type="button"
                                class="w-full p-2 text-sm text-gray-800 dark:text-gray-100 rounded text-left hover:bg-gray-100 dark:hover:bg-slate-700 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none transition-colors flex items-center gap-2"
                                @click="toggleBank(bank)"
                            >
                                <input
                                    type="checkbox"
                                    class="rounded text-10x-orange"
                                    :checked="isBankSelected(bank)"
                                    @click.stop
                                    @change="toggleBank(bank)"
                                />
                                <img
                                    v-if="bank.logo_url"
                                    :src="bank.logo_url"
                                    :alt="bank.name"
                                    class="w-5 h-5 rounded-full shrink-0 object-contain"
                                />
                                <span
                                    v-else
                                    class="w-5 h-5 rounded-full shrink-0 bg-slate-100 dark:bg-slate-700"
                                ></span>
                                <span>{{ bank.name }}</span>
                            </button>
                        </li>
                        <li v-if="selectedBanks.length >= 1">
                            <button
                                type="button"
                                class="w-full p-2 text-sm text-gray-800 dark:text-gray-100 rounded text-left hover:bg-gray-100 dark:hover:bg-slate-700 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none transition-colors flex items-center gap-2"
                                @click="store.resetInsightsBankFilter()"
                            >
                                <span>Reset</span>
                            </button>
                        </li>
                    </ul>
                </template>
            </ButtonPopover>

            <ButtonPopover
                v-if="periodOptions.length > 1"
                buttonClass="btn-base btn-white !pl-4 !pr-3 h-10"
                :active-button-class="activeFilterBtnClass"
            >
                <template #button-content>
                    <span class="text-primary text-sm font-medium"
                        >Benefit period</span
                    >
                    <Transition name="fade">
                        <span
                            v-if="selectedPeriodTypes.length >= 1"
                            class="w-5 h-5 text-white rounded-full inline-flex items-center justify-center bg-slate-800 text-xs font-medium"
                        >
                            {{ selectedPeriodTypes.length }}
                        </span>
                    </Transition>
                    <Icon
                        icon="chevron-down-small"
                        class="w-5 h-5 icons-primary"
                    />
                </template>
                <template #default>
                    <ul>
                        <li v-for="period in periodOptions" :key="period.id">
                            <button
                                type="button"
                                class="w-full p-2 text-sm text-gray-800 dark:text-gray-100 rounded text-left hover:bg-gray-100 dark:hover:bg-slate-700 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none transition-colors flex items-center gap-2"
                                @click="togglePeriod(period)"
                            >
                                <input
                                    type="checkbox"
                                    class="rounded text-10x-orange"
                                    :checked="isPeriodSelected(period)"
                                    @click.stop
                                    @change="togglePeriod(period)"
                                />
                                <span>{{ period.name }}</span>
                            </button>
                        </li>
                        <li v-if="selectedPeriodTypes.length >= 1">
                            <button
                                type="button"
                                class="w-full p-2 text-sm text-gray-800 dark:text-gray-100 rounded text-left hover:bg-gray-100 dark:hover:bg-slate-700 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none transition-colors flex items-center gap-2"
                                @click="store.resetInsightsPeriodFilter()"
                            >
                                <span>Reset</span>
                            </button>
                        </li>
                    </ul>
                </template>
            </ButtonPopover>

            <ButtonPopover buttonClass="btn-base btn-white !pl-4 !pr-3 h-10"
                :active-button-class="activeFilterBtnClass">
                <template #button-content>
                    <span class="text-primary text-sm font-medium">Usage</span>
                    <Transition name="fade">
                        <span
                            v-if="selectedUsage.length >= 1"
                            class="w-5 h-5 text-white rounded-full inline-flex items-center justify-center bg-slate-800 text-xs font-medium"
                        >
                            {{ selectedUsage.length }}
                        </span>
                    </Transition>
                    <Icon
                        icon="chevron-down-small"
                        class="w-5 h-5 icons-primary"
                    />
                </template>
                <template #default>
                    <ul>
                        <li v-for="option in usageOptions" :key="option.id">
                            <button
                                type="button"
                                class="w-full p-2 text-sm text-gray-800 dark:text-gray-100 rounded text-left hover:bg-gray-100 dark:hover:bg-slate-700 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none transition-colors flex items-center gap-2"
                                @click="toggleUsage(option)"
                            >
                                <input
                                    type="checkbox"
                                    class="rounded text-10x-orange"
                                    :checked="isUsageSelected(option)"
                                    @click.stop
                                    @change="toggleUsage(option)"
                                />
                                <span>{{ option.name }}</span>
                            </button>
                        </li>
                        <li v-if="selectedUsage.length >= 1">
                            <button
                                type="button"
                                class="w-full p-2 text-sm text-gray-800 dark:text-gray-100 rounded text-left hover:bg-gray-100 dark:hover:bg-slate-700 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none transition-colors flex items-center gap-2"
                                @click="store.resetInsightsUsageFilter()"
                            >
                                <span>Reset</span>
                            </button>
                        </li>
                    </ul>
                </template>
            </ButtonPopover>

            <ButtonPopover
                v-if="categoryOptions.length > 1"
                buttonClass="btn-base btn-white !pl-4 !pr-3 h-10"
                :active-button-class="activeFilterBtnClass"
            >
                <template #button-content>
                    <span class="text-primary text-sm font-medium"
                        >Category</span
                    >
                    <Transition name="fade">
                        <span
                            v-if="selectedCategories.length >= 1"
                            class="w-5 h-5 text-white rounded-full inline-flex items-center justify-center bg-slate-800 text-xs font-medium"
                        >
                            {{ selectedCategories.length }}
                        </span>
                    </Transition>
                    <Icon
                        icon="chevron-down-small"
                        class="w-5 h-5 icons-primary"
                    />
                </template>
                <template #default>
                    <ul>
                        <li
                            v-for="category in categoryOptions"
                            :key="category.id"
                        >
                            <button
                                type="button"
                                class="w-full p-2 text-sm text-gray-800 dark:text-gray-100 rounded text-left hover:bg-gray-100 dark:hover:bg-slate-700 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none transition-colors flex items-center gap-2"
                                @click="toggleCategory(category)"
                            >
                                <input
                                    type="checkbox"
                                    class="rounded text-10x-orange"
                                    :checked="isCategorySelected(category)"
                                    @click.stop
                                    @change="toggleCategory(category)"
                                />
                                <span>{{ category.name }}</span>
                            </button>
                        </li>
                        <li v-if="selectedCategories.length >= 1">
                            <button
                                type="button"
                                class="w-full p-2 text-sm text-gray-800 dark:text-gray-100 rounded text-left hover:bg-gray-100 dark:hover:bg-slate-700 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none transition-colors flex items-center gap-2"
                                @click="store.resetInsightsCategoryFilter()"
                            >
                                <span>Reset</span>
                            </button>
                        </li>
                    </ul>
                </template>
            </ButtonPopover>

            <button
                v-if="hasActiveInsightsFilters"
                class="btn-base btn-transparent !pl-4 !pr-3 !py-1.5"
                @click="store.clearInsightsFilters()"
            >
                <span class="text-primary text-sm font-medium">Reset all</span>
                <Icon icon="x" class="w-3 h-3 icons-primary" />
            </button>
        </div>

        <div class="hidden sm:flex items-center gap-2 lg:ml-auto">
            <div class="relative" v-click-outside="closePresetPopoverDesktop">
                <button
                    type="button"
                    class="flex items-center gap-2 pl-4 pr-3 py-2 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                    @click="presetOpen = !presetOpen"
                >
                    <Icon
                        icon="calendar"
                        class="w-5 h-5 text-slate-800 dark:text-slate-100"
                        svgClass="fill-none"
                        pathClass="fill-current"
                    />
                    <span
                        class="text-slate-800 dark:text-slate-100 font-inter text-sm font-medium leading-5"
                    >
                        {{ rangeLabel }}
                    </span>
                    <Icon
                        icon="chevron-down-small"
                        class="w-5 h-5 text-slate-800 dark:text-slate-100"
                    />
                </button>

                <PeriodPicker
                    v-if="presetOpen && !isMobile"
                    @close="closePresetPopover"
                />
            </div>

            <div
                class="flex items-center rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm"
            >
                <button
                    type="button"
                    class="flex p-2 items-center rounded-l cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:z-10"
                    aria-label="Previous period"
                    @click="store.shiftInsightsRange(-1)"
                >
                    <Icon
                        icon="chevron-left-small"
                        class="w-5 h-5 text-slate-800 dark:text-slate-100"
                    />
                </button>
                <div class="w-px self-stretch bg-slate-200 dark:bg-slate-700"></div>
                <button
                    type="button"
                    :class="[
                        'flex p-2 items-center rounded-r focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:z-10',
                        canNavigateNext
                            ? 'cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-700'
                            : 'opacity-50 cursor-default',
                    ]"
                    :disabled="!canNavigateNext"
                    aria-label="Next period"
                    @click="canNavigateNext && store.shiftInsightsRange(1)"
                >
                    <Icon
                        icon="chevron-right-small"
                        class="w-5 h-5 text-slate-800 dark:text-slate-100"
                    />
                </button>
            </div>
        </div>
    </div>

    <InsightsFiltersMobile
        :is-open="mobileFiltersOpen && isMobile"
        @close="mobileFiltersOpen = false"
    />

    <PeriodPickerMobile
        :is-open="presetOpen && isMobile"
        @close="closePresetPopover"
    />
</template>

<script setup>
import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useIsMobile } from '@/composables/useIsMobile';
import { useCardBenefitsStore } from '@/store/card-benefits';
import ButtonPopover from '@/components/ui/button-popover.vue';
import Icon from '@/components/ui/icon/icon.vue';
import InsightsCardDropdown from './insights-card-dropdown.vue';
import InsightsFiltersMobile from './insights-filters-mobile.vue';
import PeriodPicker from './period-picker.vue';
import PeriodPickerMobile from './period-picker-mobile.vue';

const store = useCardBenefitsStore();
const {
    insightsSelectedBanks: selectedBanks,
    insightsSelectedPeriodTypes: selectedPeriodTypes,
    insightsSelectedUsage: selectedUsage,
    insightsSelectedCategories: selectedCategories,
    insightsSelectedUsers: selectedUsers,
    insightsSelectedCards: selectedCards,
    insightsRangeShiftCount,
    insightsDateRangeLabel: rangeLabel,
    hasActiveInsightsFilters,
} = storeToRefs(store);

const { isMobile } = useIsMobile();
const mobileFiltersOpen = ref(false);

// Pressed/open state for filter buttons (Figma "Filters. Key Flow"):
// light slate fill while the dropdown is open. Text/chevron stay dark.
const activeFilterBtnClass =
    '!bg-slate-200 hover:!bg-slate-200 dark:!bg-slate-700 dark:hover:!bg-slate-700';

const activeFiltersCount = computed(() =>
    selectedUsers.value.length +
    selectedCards.value.length +
    selectedBanks.value.length +
    selectedPeriodTypes.value.length +
    selectedUsage.value.length +
    selectedCategories.value.length
);

const bankOptions = computed(() => store.insightsBankOptions);
const periodOptions = computed(() => store.insightsPeriodOptions);
const usageOptions = computed(() => store.insightsUsageOptions);
const categoryOptions = computed(() => store.insightsCategoryOptions);
const userOptions = computed(() => store.insightsUserOptions);

const presetOpen = ref(false);
const closePresetPopover = () => {
    presetOpen.value = false;
};
// Desktop click-outside listener fires on mobile too (the desktop wrapper is
// only visually hidden, still in the DOM with the directive bound). Ignore it
// while on mobile — the mobile modal teleports to body and would otherwise be
// considered "outside" and close on every internal tap.
const closePresetPopoverDesktop = () => {
    if (!isMobile.value) {
        closePresetPopover();
    }
};

const canNavigateNext = computed(() => insightsRangeShiftCount.value > 0);

const isBankSelected = (bank) =>
    selectedBanks.value.some((b) => b.id === bank.id);
const toggleBank = (bank) => store.toggleInsightsBankFilter(bank);

const isPeriodSelected = (period) =>
    selectedPeriodTypes.value.some((p) => p.id === period.id);
const togglePeriod = (period) => store.toggleInsightsPeriodFilter(period);

const isUsageSelected = (option) =>
    selectedUsage.value.some((u) => u.id === option.id);
const toggleUsage = (option) => store.toggleInsightsUsageFilter(option);

const isCategorySelected = (category) =>
    selectedCategories.value.some((c) => c.id === category.id);
const toggleCategory = (category) =>
    store.toggleInsightsCategoryFilter(category);

const isUserSelected = (user) =>
    selectedUsers.value.some((u) => u.id === user.id);
const toggleUser = (user) => store.toggleInsightsUserFilter(user);
</script>
